import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { ArrowRight, Lock, AlertCircle } from "lucide-react";

export default function SuperAdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [prevSession, setPrevSession] = useState(null); // {email, role} if a non-SA session is already active
  const [checking, setChecking] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (data?.role === "superadmin") {
          // Already SA — go straight in
          nav("/superadmin", { replace: true });
          return;
        }
        // Non-SA session exists (Google auto-login or previous login)
        setPrevSession({ email: data.email, name: data.name, role: data.role });
      } catch {
        // No session — normal case
      } finally {
        setChecking(false);
      }
    })();
  }, [nav]);

  async function forceLogoutAndContinue() {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    setPrevSession(null);
    toast.success("Sessione precedente chiusa. Ora accedi come Super Admin.");
  }

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      // Force-logout any existing non-SA session before the SA login attempt,
      // so the SA cookie replaces the previous one cleanly.
      if (prevSession) {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
      }
      await api.post("/superadmin/login", { email: email.trim(), password });
      toast.success("Accesso Super Admin autorizzato");
      nav("/superadmin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0b1120] text-slate-100 relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute top-0 -left-32 w-[600px] h-[600px] bg-rose-500/10 rounded-full blur-3xl"/>
        <div className="absolute bottom-0 -right-32 w-[600px] h-[600px] bg-sky-500/10 rounded-full blur-3xl"/>
      </div>

      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center mb-8">
            <img
              src="/brand/logo-256.png"
              alt="MyFantabusiness"
              className="w-24 h-24 object-contain drop-shadow-[0_8px_24px_rgba(244,63,94,0.4)]"
            />
          </div>

          <div className="text-center mb-8">
            <div className="text-[11px] tracking-[0.4em] text-rose-400 uppercase font-bold mb-2">MyFantaBusiness</div>
            <h1 className="text-3xl font-black tracking-tight" data-testid="superadmin-login-title">
              Super Admin Console
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              Accesso riservato agli amministratori della piattaforma
            </p>
          </div>

          {checking ? (
            <div className="text-center text-slate-500 text-sm py-8">Verifica sessione…</div>
          ) : (
            <>
              {prevSession && (
                <div
                  data-testid="sa-prev-session-warn"
                  className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm"
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle size={18} className="text-amber-400 mt-0.5 shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-amber-200">Sessione attiva rilevata</div>
                      <div className="mt-1 text-amber-100/80 text-xs">
                        Sei attualmente loggato come <span className="font-mono text-white">{prevSession.email}</span>
                        {prevSession.role && prevSession.role !== "user" && <> ({prevSession.role})</>}.
                        Per accedere come Super Admin devi prima chiudere questa sessione.
                      </div>
                      <button
                        type="button"
                        onClick={forceLogoutAndContinue}
                        data-testid="sa-force-logout"
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs"
                      >
                        Chiudi sessione e procedi
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={onSubmit} className="bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-xl p-6 space-y-4 shadow-2xl">
            <div>
              <label className="text-xs tracking-widest text-slate-400 uppercase font-bold block mb-2">
                Email
              </label>
              <input
                data-testid="superadmin-login-email"
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-4 py-3 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500 transition-colors"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@dominio.it"
              />
            </div>
            <div>
              <label className="text-xs tracking-widest text-slate-400 uppercase font-bold block mb-2">
                Password
              </label>
              <input
                data-testid="superadmin-login-password"
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-4 py-3 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500 transition-colors"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="superadmin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-rose-500 hover:bg-rose-400 text-white font-bold uppercase tracking-wider disabled:opacity-50 shadow-lg shadow-rose-500/20 transition-all"
            >
              {loading ? "Verifica…" : "Accedi"} <ArrowRight size={16}/>
            </button>

            <div className="pt-2 flex items-center gap-2 text-xs text-slate-500">
              <Lock size={12}/> Sessione protetta con JWT httpOnly
            </div>
          </form>
            </>
          )}

          <div className="mt-6 text-center text-xs text-slate-500">
            © 2026 MyFantaBusiness · Console di controllo piattaforma
          </div>
        </div>
      </div>
    </div>
  );
}
